import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useState } from 'react';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { updateAgentURL } from '../../../../utils/urls';

const UpdateAgent = ({ visible, onHide, agent, onAgentUpdated }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        idNumber: '',
        department: '',
        team_id: '',
        role: '',
        status: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const departments = [
        { label: 'Customer Service', value: 'customer_service' },
        { label: 'Technical Support', value: 'technical_support' },
        { label: 'Billing', value: 'billing' },
        { label: 'General Inquiry', value: 'general_inquiry' }
    ];

    const roles = [
        { label: 'Call Center Agent', value: 'call_center_agent' },
        { label: 'Team Leader', value: 'team_leader' },
        { label: 'Technician', value: 'technician' },
        { label: 'Supervisor', value: 'supervisor' },
        { label: 'Manager', value: 'manager' }
    ];

    const statuses = [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'On Break', value: 'on_break' },
        { label: 'Offline', value: 'offline' }
    ];

    useEffect(() => {
        if (agent && visible) {
            setFormData({
                firstName: agent.firstName || '',
                lastName: agent.lastName || '',
                email: agent.email || '',
                phoneNumber: agent.phoneNumber || agent.phone || '',
                idNumber: agent.idNumber || agent.id_number || '',
                department: agent.department || '',
                team_id: agent.team_id || '',
                role: agent.role || '',
                status: agent.status || ''
            });
            setSubmitted(false);
            setValidationErrors({});
        }
    }, [agent, visible]);

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const validateName = (name) => {
        // Only allow letters and spaces, no numbers or special characters
        const nameRegex = /^[a-zA-Z\s]+$/;
        return nameRegex.test(name);
    };

    const validatePhoneNumber = (phone) => {
        // Allow numbers, spaces, hyphens, parentheses, and plus sign
        const phoneRegex = /^[\d\s\-\+\(\)]+$/;
        return phoneRegex.test(phone);
    };

    const handleInputChange = (e, field) => {
        let val = (e.target && e.target.value) || '';
        const errors = { ...validationErrors };

        // Handle name fields with capitalization and validation
        if (field === 'firstName' || field === 'lastName') {
            if (val && !validateName(val)) {
                errors[field] = 'Name should only contain letters and spaces';
            } else {
                delete errors[field];
                // Capitalize first letter of each word
                val = val.split(' ').map(word => capitalizeFirstLetter(word)).join(' ');
            }
        }

        // Validate phone number
        if (field === 'phoneNumber' && val && !validatePhoneNumber(val)) {
            errors.phoneNumber = 'Invalid phone number format';
        } else if (field === 'phoneNumber') {
            delete errors.phoneNumber;
        }

        setValidationErrors(errors);
        setFormData(prev => ({
            ...prev,
            [field]: val
        }));
    };

    const updateAgent = async () => {
        setSubmitted(true);

        // Validate required fields
        if (!formData.firstName?.trim() || !formData.lastName?.trim() || !formData.email?.trim() || !formData.phoneNumber?.trim() || !formData.idNumber?.trim()) {
            toastify('Please fill in all required fields', 'error');
            return;
        }

        // Check for validation errors
        if (Object.keys(validationErrors).length > 0) {
            toastify('Please fix validation errors before submitting', 'error');
            return;
        }

        setLoading(true);

        try {
            const url = updateAgentURL.replace(':id', agent._id);
            const response = await makeRequest2(url, 'POST', formData);

            if (response.success !== false) {
                toastify('Agent updated successfully', 'success');
                onAgentUpdated();
                onHide();
            } else {
                toastify(response.message || response.error || 'Failed to update agent', 'error');
            }
        } catch (error) {
            toastify(error.message || 'Failed to update agent', 'error');
        } finally {
            setLoading(false);
        }
    };

    const dialogFooter = (
        <>
            <Button
                label="Cancel"
                icon="pi pi-times"
                outlined
                onClick={onHide}
                disabled={loading}
            />
            <Button
                label="Update"
                icon="pi pi-check"
                onClick={updateAgent}
                loading={loading}
            />
        </>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: '450px' }}
            header="Update Agent"
            modal
            className="p-fluid"
            footer={dialogFooter}
            onHide={onHide}
        >
            <div className="field">
                <label htmlFor="firstName">First Name *</label>
                <InputText
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange(e, 'firstName')}
                    required
                    autoFocus
                    className={(submitted && !formData.firstName) || validationErrors.firstName ? 'p-invalid' : ''}
                />
                {submitted && !formData.firstName && <small className="p-error">First Name is required.</small>}
                {validationErrors.firstName && <small className="p-error">{validationErrors.firstName}</small>}
            </div>

            <div className="field">
                <label htmlFor="lastName">Last Name *</label>
                <InputText
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange(e, 'lastName')}
                    required
                    className={(submitted && !formData.lastName) || validationErrors.lastName ? 'p-invalid' : ''}
                />
                {submitted && !formData.lastName && <small className="p-error">Last Name is required.</small>}
                {validationErrors.lastName && <small className="p-error">{validationErrors.lastName}</small>}
            </div>

            <div className="field">
                <label htmlFor="email">Email *</label>
                <InputText
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange(e, 'email')}
                    required
                    type="email"
                    className={submitted && !formData.email ? 'p-invalid' : ''}
                />
                {submitted && !formData.email && <small className="p-error">Email is required.</small>}
            </div>

            <div className="field">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <InputText
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange(e, 'phoneNumber')}
                    required
                    className={(submitted && !formData.phoneNumber) || validationErrors.phoneNumber ? 'p-invalid' : ''}
                />
                {submitted && !formData.phoneNumber && <small className="p-error">Phone Number is required.</small>}
                {validationErrors.phoneNumber && <small className="p-error">{validationErrors.phoneNumber}</small>}
            </div>

            <div className="field">
                <label htmlFor="idNumber">ID/Passport Number *</label>
                <InputText
                    id="idNumber"
                    value={formData.idNumber}
                    onChange={(e) => handleInputChange(e, 'idNumber')}
                    required
                    className={submitted && !formData.idNumber ? 'p-invalid' : ''}
                />
                {submitted && !formData.idNumber && <small className="p-error">ID/Passport Number is required.</small>}
            </div>

            <div className="field">
                <label htmlFor="department">Department</label>
                <Dropdown
                    id="department"
                    value={formData.department}
                    onChange={(e) => handleInputChange(e, 'department')}
                    options={departments}
                    placeholder="Select Department"
                />
            </div>

            <div className="field">
                <label htmlFor="team_id">Team ID</label>
                <InputText
                    id="team_id"
                    value={formData.team_id}
                    onChange={(e) => handleInputChange(e, 'team_id')}
                />
            </div>

            <div className="field">
                <label htmlFor="role">Role</label>
                <Dropdown
                    id="role"
                    value={formData.role}
                    onChange={(e) => handleInputChange(e, 'role')}
                    options={roles}
                    placeholder="Select Role"
                />
            </div>

            <div className="field">
                <label htmlFor="status">Status</label>
                <Dropdown
                    id="status"
                    value={formData.status}
                    onChange={(e) => handleInputChange(e, 'status')}
                    options={statuses}
                    placeholder="Select Status"
                />
            </div>
        </Dialog>
    );
};

export default UpdateAgent;